import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ParcelamentoAPI } from '../types/parcelling.types';

interface ParcellingState {
	parcellings: ParcelamentoAPI[];
	selectedParcellingId: string | null;
	isLoading: boolean;
	error: string | null;
	searchTerm: string;

	setParcellings: (parcellings: ParcelamentoAPI[]) => void;
	addParcelling: (parcelling: ParcelamentoAPI) => void;
	updateParcelling: (id: string, parcelling: Partial<ParcelamentoAPI>) => void;
	removeParcelling: (id: string) => void;
	selectParcelling: (id: string | null) => void;
	setLoading: (isLoading: boolean) => void;
	setError: (error: string | null) => void;
	setSearchTerm: (term: string) => void;
	getParcellingById: (id: string) => ParcelamentoAPI | undefined;
	getSelectedParcelling: () => ParcelamentoAPI | undefined;
	getFilteredParcellings: () => ParcelamentoAPI[];
	reset: () => void;
}

const initialState = {
	parcellings: [],
	selectedParcellingId: null,
	isLoading: false,
	error: null,
	searchTerm: '',
};

export const useParcellingStore = create<ParcellingState>()(
	devtools(
		(set, get) => ({
			...initialState,

			setParcellings: (parcellings) => set({ parcellings }),

			addParcelling: (parcelling) =>
				set((state) => ({
					parcellings: [parcelling, ...state.parcellings],
				})),

			updateParcelling: (id, updates) =>
				set((state) => ({
					parcellings: state.parcellings.map((p) =>
						p._id === id ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p
					),
				})),

			removeParcelling: (id) =>
				set((state) => ({
					parcellings: state.parcellings.filter((p) => p._id !== id),
					selectedParcellingId:
						state.selectedParcellingId === id ? null : state.selectedParcellingId,
				})),

			selectParcelling: (id) => set({ selectedParcellingId: id }),

			setLoading: (isLoading) => set({ isLoading }),

			setError: (error) => set({ error }),

			setSearchTerm: (searchTerm) => set({ searchTerm }),

			getParcellingById: (id) => {
				return get().parcellings.find((p) => p._id === id);
			},

			getSelectedParcelling: () => {
				const { selectedParcellingId, parcellings } = get();
				if (!selectedParcellingId) return undefined;
				return parcellings.find((p) => p._id === selectedParcellingId);
			},

			getFilteredParcellings: () => {
				const { parcellings, searchTerm } = get();
				if (!searchTerm.trim()) return parcellings;

				const term = searchTerm.toLowerCase();
				return parcellings.filter(
					(p) =>
						p.name.toLowerCase().includes(term) ||
						p.culture.toLowerCase().includes(term) ||
						p.cropStage.toLowerCase().includes(term) ||
						p.note?.toLowerCase().includes(term)
				);
			},

			reset: () => set(initialState),
		}),
		{ name: 'ParcellingStore' }
	)
);
